<template>
  <div class="wykazpojazdy-page">
    <div class="page-header">
      <div class="page-header-text">
        <h1 class="page-title">Wykaz pojazdów</h1>
      </div>
    </div>
    <!-- City selector -->
    <v-row class="mb-2" align="center">
      <v-col cols="12" md="4">
        <v-select
          v-model="selectedSubCompanyId"
          :items="subCompanies"
          item-text="name"
          item-value="id"
          :label="$t('wykazPojazdy.cityBranch')"
          clearable
          dense
          outlined
          hide-details
          prepend-inner-icon="mdi-city"
        ></v-select>
      </v-col>
    </v-row>

    <v-tabs v-model="activeTab" class="mb-4">
      <v-tab>{{ $t('wykazPojazdy.activeTab') }} ({{ activeTotal }})</v-tab>
      <v-tab>
        {{ $t('wykazPojazdy.pendingTab') }}
        <v-badge v-if="pendingCount > 0" :content="pendingCount" color="orange" inline></v-badge>
      </v-tab>
      <v-tab>{{ $t('wykazPojazdy.archiveTab') }} ({{ archiveTotal }})</v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab" touchless>
      <!-- TAB: ACTIVE -->
      <v-tab-item>
        <v-data-table
          :headers="activeHeaders"
          :items="activeItems"
          :loading="loading"
          :options.sync="activeOptions"
          :server-items-length="activeTotal"
          :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
          class="elevation-1"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <v-toolbar-title>{{ $t('wykaz.vehicles.title') }}</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-text-field v-model="search" :label="$t('common.search') + '...'" single-line hide-details clearable class="mr-4" style="max-width:300px" prepend-inner-icon="mdi-magnify"></v-text-field>
              <v-btn color="blue darken-2" dark class="mr-2" @click="downloadExcel"><v-icon left>mdi-file-excel</v-icon>Excel</v-btn>
              <v-btn color="red darken-2" dark class="mr-2" @click="downloadPdf"><v-icon left>mdi-file-pdf-box</v-icon>PDF</v-btn>
<v-btn color="primary" dark @click="openCreateDialog"><v-icon left>mdi-plus</v-icon>{{ $t('wykaz.vehicles.addButton') }}</v-btn>
            </v-toolbar>
          </template>
          <template v-slot:[`item.dataZgloszenia`]="{ item }">{{ formatDate(item.dataZgloszenia) }}</template>
          <template v-slot:[`item.miasta`]="{ item }">
            <v-chip
              v-for="m in item.miasta"
              :key="m.id"
              x-small
              class="mr-1"
              :color="m.id === item.id ? 'primary' : 'grey darken-2'"
              :title="statusMiasta(m)"
            >{{ m.nazwa }}</v-chip>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn icon small color="primary" @click="openDetailDialog(item)"><v-icon small>mdi-eye</v-icon></v-btn>
            <v-btn icon small color="orange" @click="openEditDialog(item)"><v-icon small>mdi-pencil</v-icon></v-btn>
            <v-btn icon small color="teal" title="Zgłoś w innym mieście" @click="openCityDialog(item)"><v-icon small>mdi-city-variant-outline</v-icon></v-btn>
            <v-btn icon small color="red" @click="confirmDeactivate(item)"><v-icon small>mdi-archive-arrow-down</v-icon></v-btn>
          </template>
        </v-data-table>
      </v-tab-item>

      <!-- TAB: PENDING -->
      <v-tab-item>
        <v-data-table
          :headers="pendingHeaders"
          :items="pendingItems"
          :loading="pendingLoading"
          :options.sync="pendingOptions"
          :server-items-length="pendingTotal"
          :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
          class="elevation-1"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <v-toolbar-title>{{ $t('wykazPojazdy.pendingApproval') }}</v-toolbar-title>
            </v-toolbar>
          </template>
          <template v-slot:[`item.createdAt`]="{ item }">{{ formatDate(item.createdAt) }}</template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn icon small color="primary" @click="openDetailDialog(item)"><v-icon small>mdi-eye</v-icon></v-btn>
            <v-btn icon small color="orange" @click="openEditDialog(item)"><v-icon small>mdi-pencil</v-icon></v-btn>
            <v-btn small color="success" class="mr-1" @click="confirmApprove(item)"><v-icon left small>mdi-check</v-icon>{{ $t('common.approve') }}</v-btn>
            <v-btn small color="error" @click="openRejectDialog(item)"><v-icon left small>mdi-close</v-icon>{{ $t('common.reject') }}</v-btn>
          </template>
        </v-data-table>
      </v-tab-item>

      <!-- TAB: ARCHIVE -->
      <v-tab-item>
        <v-data-table
          :headers="archiveHeaders"
          :items="archiveItems"
          :loading="archiveLoading"
          :options.sync="archiveOptions"
          :server-items-length="archiveTotal"
          :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
          class="elevation-1"
        >
          <template v-slot:top>
            <v-toolbar flat><v-toolbar-title>{{ $t('wykazPojazdy.vehicleArchive') }}</v-toolbar-title></v-toolbar>
          </template>
          <template v-slot:[`item.dataDezaktywacji`]="{ item }">{{ formatDate(item.dataDezaktywacji) }}</template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn small color="success" @click="confirmRestore(item)"><v-icon left small>mdi-archive-arrow-up</v-icon>{{ $t('wykazPojazdy.restore') }}</v-btn>
          </template>
        </v-data-table>
      </v-tab-item>
    </v-tabs-items>

    <!-- CREATE/EDIT DIALOG -->
    <v-dialog content-class="dark-form-dialog" v-model="formDialog" max-width="700px">
      <v-card>
        <v-card-title>{{ editMode ? $t('wykazPojazdy.editVehicle') : $t('wykazPojazdy.addNewVehicle') }}</v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="formValid">
            <v-row>
              <v-col cols="12" md="4">
                <v-select v-model="formData.subCompanyId" :items="subCompanies" item-text="name" item-value="id" :label="$t('wykazPojazdy.cityBranch') + ' *'" :rules="[rules.required]" required></v-select>
              </v-col>
              <v-col cols="12" md="2" v-if="editMode">
                <v-text-field v-model.number="formData.lp" label="Lp." type="number" outlined dense></v-text-field>
              </v-col>
              <v-col cols="12" :md="editMode ? 6 : 6">
                <v-text-field v-model="formData.markaModel" label="Marka i model *" :rules="[rules.required]" required></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="formData.nrRejestracyjny" label="Nr rejestracyjny *" :rules="[rules.required]" required></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="formData.numerVin" label="Numer VIN"></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="formData.tytulPrawny" label="Tytuł prawny" hint='np. "najem od 01.03.2026"' persistent-hint></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="formData.nrBoczny" label="Nr boczny"></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-combobox v-model="formData.kierowca" :items="driverOptions" label="Kierowca" clearable hide-no-data hint="Wybierz z listy lub wpisz" persistent-hint></v-combobox>
              </v-col>
              <v-col cols="12" md="4">
                <v-menu v-model="dateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="formData.dataZgloszenia" label="Data zgłoszenia" prepend-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" clearable></v-text-field>
                  </template>
                  <v-date-picker v-model="formData.dataZgloszenia" @input="dateMenu = false" :locale="$i18n.locale"></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="formDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="submitForm" :disabled="!formValid">{{ editMode ? $t('common.save') : $t('common.add') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DETAIL DIALOG -->
    <v-dialog content-class="dark-form-dialog" v-model="detailDialog" max-width="600px">
      <v-card v-if="detailItem">
        <v-card-title>{{ detailItem.markaModel }} — {{ detailItem.nrRejestracyjny }}</v-card-title>
        <v-card-text>
          <v-simple-table>
            <tbody>
              <tr><td class="font-weight-bold">Lp.</td><td>{{ detailItem.lp || '-' }}</td></tr>
              <tr><td class="font-weight-bold">Marka i model</td><td>{{ detailItem.markaModel }}</td></tr>
              <tr><td class="font-weight-bold">Nr rejestracyjny</td><td>{{ detailItem.nrRejestracyjny }}</td></tr>
              <tr><td class="font-weight-bold">Numer VIN</td><td>{{ detailItem.numerVin || '-' }}</td></tr>
              <tr><td class="font-weight-bold">Tytuł prawny</td><td>{{ detailItem.tytulPrawny || '-' }}</td></tr>
              <tr><td class="font-weight-bold">Nr boczny</td><td>{{ detailItem.nrBoczny || '-' }}</td></tr>
              <tr><td class="font-weight-bold">Kierowca</td><td>{{ detailItem.kierowca || '-' }}</td></tr>
              <tr><td class="font-weight-bold">Data zgłoszenia</td><td>{{ formatDate(detailItem.dataZgloszenia) }}</td></tr>
              <tr><td class="font-weight-bold">Status</td><td>{{ detailItem.status }}</td></tr>
              <tr v-if="detailItem.dodal"><td class="font-weight-bold">Dodał</td><td>{{ detailItem.dodal }}</td></tr>
              <tr v-if="detailItem.zatwierdzil"><td class="font-weight-bold">Zatwierdził</td><td>{{ detailItem.zatwierdzil }}</td></tr>
              <tr v-if="detailItem.powodOdrzucenia"><td class="font-weight-bold">Powód odrzucenia</td><td class="red--text">{{ detailItem.powodOdrzucenia }}</td></tr>
              <tr v-if="detailItem.miasta && detailItem.miasta.length">
                <td class="font-weight-bold">Zgłoszone w miastach</td>
                <td>
                  <div v-for="m in detailItem.miasta" :key="m.id">
                    {{ m.nazwa }} — {{ m.status }}<span v-if="m.lp">, Lp. {{ m.lp }}</span><span v-if="m.nrBoczny">, nr boczny {{ m.nrBoczny }}</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-card-actions><v-spacer></v-spacer><v-btn text @click="detailDialog = false">{{ $t('common.close') }}</v-btn></v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ZGŁOSZENIE W KOLEJNYM MIEŚCIE -->
    <v-dialog content-class="dark-form-dialog" v-model="cityDialog" max-width="600px">
      <v-card v-if="cityItem">
        <v-card-title>Zgłoś w innym mieście</v-card-title>
        <v-card-text>
          <p class="mb-3">
            Pojazd <b>{{ cityItem.nrRejestracyjny }}</b> ({{ cityItem.markaModel }}) zostanie zgłoszony
            w kolejnym mieście. Marka, VIN i tytuł prawny zostają wspólne — miasto nadaje własne
            Lp. i numer boczny przy akceptacji.
          </p>
          <v-form ref="cityForm" v-model="cityValid">
            <v-row>
              <v-col cols="12" md="6">
                <v-select v-model="cityData.subCompanyId" :items="wolneMiasta" item-text="name" item-value="id" label="Miasto / oddział *" :rules="[rules.required]" required></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="cityData.nrBoczny" label="Nr boczny w tym mieście"></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-combobox v-model="cityData.kierowca" :items="driverOptions" label="Kierowca" clearable hide-no-data></v-combobox>
              </v-col>
              <v-col cols="12" md="6">
                <v-menu v-model="cityDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="cityData.dataZgloszenia" label="Data zgłoszenia" prepend-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" clearable></v-text-field>
                  </template>
                  <v-date-picker v-model="cityData.dataZgloszenia" @input="cityDateMenu = false" :locale="$i18n.locale"></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
          </v-form>
          <v-alert v-if="wolneMiasta.length === 0" type="info" dense text class="mb-0">
            Ten pojazd jest już zgłoszony we wszystkich oddziałach.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cityDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="submitCity" :disabled="!cityData.subCompanyId">Zgłoś</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- REJECT DIALOG -->
    <v-dialog content-class="dark-form-dialog" v-model="rejectDialog" max-width="500px">
      <v-card>
        <v-card-title>{{ $t('wykazPojazdy.rejectEntry') }}</v-card-title>
        <v-card-text>
          <p>{{ $t('wykazPojazdy.vehicleLabel') }}: <b>{{ rejectItem?.nrRejestracyjny }}</b></p>
          <v-textarea v-model="rejectReason" :label="$t('wykazPojazdy.rejectionReason') + ' *'" :rules="[rules.required]" outlined rows="3"></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="rejectDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="error" @click="submitReject" :disabled="!rejectReason">{{ $t('wykazPojazdy.rejectEntry') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import Swal from "sweetalert2";
const axios = require("axios");

export default {
  name: "WykazPojazdyPage",
  data() {
    return {
      activeTab: 0,
      syncing: false,
      subCompanies: [],
      selectedSubCompanyId: null,
      // Active
      activeItems: [], loading: false, activeTotal: 0,
      search: "",
      activeOptions: { page: 1, itemsPerPage: 10 },
      activeHeaders: [
        { text: "Lp.", value: "lp", width: "60px" },
        { text: "Marka i model", value: "markaModel" },
        { text: "Nr rejestracyjny", value: "nrRejestracyjny" },
        { text: "VIN", value: "numerVin" },
        { text: "Nr boczny", value: "nrBoczny" },
        { text: "Kierowca", value: "kierowca" },
        { text: "Miasta", value: "miasta", sortable: false },
        { text: "Data zgłoszenia", value: "dataZgloszenia" },
        { text: "Akcje", value: "actions", sortable: false, width: "180px" },
      ],
      // Pending
      pendingItems: [], pendingLoading: false, pendingTotal: 0, pendingCount: 0,
      pendingOptions: { page: 1, itemsPerPage: 10 },
      pendingHeaders: [
        { text: "Marka i model", value: "markaModel" },
        { text: "Nr rejestracyjny", value: "nrRejestracyjny" },
        { text: "Kierowca", value: "kierowca" },
        { text: "Dodał", value: "dodal" },
        { text: "Data dodania", value: "createdAt" },
        { text: "Akcje", value: "actions", sortable: false, width: "280px" },
      ],
      // Archive
      archiveItems: [], archiveLoading: false, archiveTotal: 0,
      archiveOptions: { page: 1, itemsPerPage: 10 },
      archiveHeaders: [
        { text: "Lp. (oryg.)", value: "lp", width: "80px" },
        { text: "Marka i model", value: "markaModel" },
        { text: "Nr rejestracyjny", value: "nrRejestracyjny" },
        { text: "Kierowca", value: "kierowca" },
        { text: "Data dezaktywacji", value: "dataDezaktywacji" },
        { text: "Dezaktywował", value: "dezaktywowal" },
        { text: "Akcje", value: "actions", sortable: false, width: "140px" },
      ],
      // Form
      formDialog: false, formValid: false, editMode: false, editId: null, dateMenu: false,
      formData: { subCompanyId: "", lp: null, markaModel: "", nrRejestracyjny: "", numerVin: "", tytulPrawny: "", nrBoczny: "", kierowca: "", dataZgloszenia: "" },
      driverOptions: [], driversLoaded: false,
      rules: { required: (v) => !!v || "Pole wymagane" },
      // Zgłoszenie w kolejnym mieście
      cityDialog: false, cityValid: false, cityItem: null, cityDateMenu: false,
      cityData: { subCompanyId: "", nrBoczny: "", kierowca: "", dataZgloszenia: "" },
      // Detail
      detailDialog: false, detailItem: null,
      // Reject
      rejectDialog: false, rejectItem: null, rejectReason: "",
    };
  },
  computed: {
    // Miasta, w których tego auta jeszcze nie zgłoszono — w pozostałych wpis już
    // jest i backend i tak odbiłby duplikat.
    wolneMiasta() {
      if (!this.cityItem) return [];
      const zajete = (this.cityItem.miasta || []).map((m) => m.subCompanyId);
      return this.subCompanies.filter((s) => !zajete.includes(s.id));
    },
  },
  watch: {
    activeOptions: { handler() { this.fetchActive(); }, deep: true },
    search() { this.activeOptions.page = 1; this.fetchActive(); },
    pendingOptions: { handler() { this.fetchPending(); }, deep: true },
    archiveOptions: { handler() { this.fetchArchive(); }, deep: true },
    activeTab(val) {
      if (val === 1) this.fetchPending();
      if (val === 2) this.fetchArchive();
    },
    selectedSubCompanyId() {
      this.activeOptions.page = 1;
      this.pendingOptions.page = 1;
      this.archiveOptions.page = 1;
      this.fetchActive();
      this.fetchPendingCount();
      if (this.activeTab === 1) this.fetchPending();
      if (this.activeTab === 2) this.fetchArchive();
    },
  },
  async created() {
    await this.fetchSubCompanies();
    await this.fetchActive();
    await this.fetchPendingCount();
  },
  methods: {
    async syncFromCars() {
      const confirm = await Swal.fire({
        title: "Synchronizuj z floty",
        text: "Dodaj do wykazu wszystkie pojazdy z floty, których jeszcze nie ma w wykazie.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Synchronizuj",
        cancelButtonText: "Anuluj",
      });
      if (!confirm.isConfirmed) return;
      this.syncing = true;
      try {
        const r = await axios({ method: "post", url: process.env.VUE_APP_ROOT_API + "registry/vehicles/sync", headers: this.getHeaders() });
        const data = r.data.data;
        Swal.fire("Gotowe", `Dodano: ${data.created}, pominięto (już w wykazie): ${data.skipped}, łącznie pojazdów: ${data.total}`, "success");
        this.fetchActive(); this.fetchPending(); this.fetchPendingCount();
      } catch (e) { Swal.fire("Błąd", e.response?.data?.message || "Błąd synchronizacji", "error"); }
      this.syncing = false;
    },
    getHeaders() {
      return { Authorization: "Bearer " + localStorage.getItem("token"), "x-current-page": "wykaz-pojazdy" };
    },
    formatDate(d) { return d ? new Date(d).toLocaleDateString("pl-PL") : "-"; },
    buildParams(opts, extraSearch) {
      const params = { take: opts.itemsPerPage, skip: opts.page, search: extraSearch || "" };
      if (this.selectedSubCompanyId) params.subCompanyId = this.selectedSubCompanyId;
      if (opts.sortBy?.length) { params.sortBy = opts.sortBy[0]; params.sortOrder = opts.sortDesc?.[0] ? "desc" : "asc"; }
      return params;
    },
    async fetchSubCompanies() {
      try {
        const res = await axios({ method: "get", url: process.env.VUE_APP_ROOT_API + "registry/vehicles/sub-companies", headers: this.getHeaders() });
        this.subCompanies = (res.data.data || []).filter(s => !s.deleted);
      } catch (e) { console.error(e); }
    },
    // Lista kierowców do przypisania (nazwiska) — ładowana raz, leniwie przy
    // pierwszym otwarciu formularza (żeby nie spowalniać wejścia na stronę).
    async loadDrivers() {
      if (this.driversLoaded) return;
      try {
        const r = await axios.get(process.env.VUE_APP_ROOT_API + "drivers?take=10000&skip=1&search=", { headers: this.getHeaders() });
        const list = (r.data.data.result || []);
        const names = list.map((d) => `${d.firstName || ""} ${d.lastName || ""}`.trim()).filter(Boolean);
        this.driverOptions = Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, "pl"));
        this.driversLoaded = true;
      } catch (e) { console.error(e); }
    },
    async fetchActive() {
      this.loading = true;
      try {
        const res = await axios({ method: "get", url: process.env.VUE_APP_ROOT_API + "registry/vehicles", headers: this.getHeaders(), params: this.buildParams(this.activeOptions, this.search) });
        this.activeItems = res.data.data.result;
        this.activeTotal = res.data.data.count;
      } catch (e) { console.error(e); }
      this.loading = false;
    },
    async fetchPending() {
      this.pendingLoading = true;
      try {
        const res = await axios({ method: "get", url: process.env.VUE_APP_ROOT_API + "registry/vehicles/pending", headers: this.getHeaders(), params: this.buildParams(this.pendingOptions) });
        this.pendingItems = res.data.data.result;
        this.pendingTotal = res.data.data.count;
      } catch (e) { console.error(e); }
      this.pendingLoading = false;
    },
    async fetchPendingCount() {
      try {
        const params = {};
        if (this.selectedSubCompanyId) params.subCompanyId = this.selectedSubCompanyId;
        const res = await axios({ method: "get", url: process.env.VUE_APP_ROOT_API + "registry/vehicles/pending/count", headers: this.getHeaders(), params });
        this.pendingCount = res.data.data;
      } catch (e) { console.error(e); }
    },
    async fetchArchive() {
      this.archiveLoading = true;
      try {
        const res = await axios({ method: "get", url: process.env.VUE_APP_ROOT_API + "registry/vehicles/archive", headers: this.getHeaders(), params: this.buildParams(this.archiveOptions) });
        this.archiveItems = res.data.data.result;
        this.archiveTotal = res.data.data.count;
      } catch (e) { console.error(e); }
      this.archiveLoading = false;
    },
    openCreateDialog() {
      this.editMode = false; this.editId = null;
      this.formData = { subCompanyId: this.selectedSubCompanyId || "", markaModel: "", nrRejestracyjny: "", numerVin: "", tytulPrawny: "", nrBoczny: "", kierowca: "", dataZgloszenia: "" };
      this.loadDrivers();
      this.formDialog = true;
    },
    async openEditDialog(item) {
      this.editMode = true; this.editId = item.id;
      this.loadDrivers();
      try {
        const res = await axios({ method: "get", url: process.env.VUE_APP_ROOT_API + "registry/vehicles/" + item.id, headers: this.getHeaders() });
        const d = res.data.data;
        this.formData = { subCompanyId: d.subCompanyId || "", lp: d.lp || null, markaModel: d.markaModel, nrRejestracyjny: d.nrRejestracyjny, numerVin: d.numerVin || "", tytulPrawny: d.tytulPrawny || "", nrBoczny: d.nrBoczny || "", kierowca: d.kierowca || "", dataZgloszenia: d.dataZgloszenia ? d.dataZgloszenia.substring(0, 10) : "" };
        this.formDialog = true;
      } catch (e) { Swal.fire("Błąd", "Nie udało się pobrać danych", "error"); }
    },
    async openDetailDialog(item) {
      try {
        const res = await axios({ method: "get", url: process.env.VUE_APP_ROOT_API + "registry/vehicles/" + item.id, headers: this.getHeaders() });
        this.detailItem = res.data.data; this.detailDialog = true;
      } catch (e) { console.error(e); }
    },
    async submitForm() {
      if (!this.$refs.form.validate()) return;
      try {
        const payload = { ...this.formData };
        if (!payload.numerVin) delete payload.numerVin;
        if (!payload.dataZgloszenia) delete payload.dataZgloszenia;
        if (this.editMode) {
          await axios({ method: "put", url: process.env.VUE_APP_ROOT_API + "registry/vehicles/" + this.editId, headers: this.getHeaders(), data: payload });
        } else {
          await axios({ method: "post", url: process.env.VUE_APP_ROOT_API + "registry/vehicles", headers: this.getHeaders(), data: payload });
        }
        Swal.fire("Sukces", this.editMode ? "Zaktualizowano" : "Dodano do kolejki zatwierdzania", "success");
        this.formDialog = false; this.fetchActive(); this.fetchPending(); this.fetchPendingCount();
      } catch (e) { Swal.fire("Błąd", e.response?.data?.message || "Wystąpił błąd", "error"); }
    },
    async confirmApprove(item) {
      const r = await Swal.fire({ title: "Zatwierdź pojazd?", html: `Pojazd <b>${item.nrRejestracyjny}</b> zostanie dodany do oficjalnego wykazu.<br><br>Podaj Lp. lub zostaw puste (automatycznie):`, icon: "question", input: "number", inputAttributes: { min: 1, step: 1 }, inputPlaceholder: "Puste = automatycznie", showCancelButton: true, confirmButtonText: "Zatwierdź", cancelButtonText: "Anuluj" });
      if (!r.isConfirmed) return;
      try {
        await axios({ method: "post", url: process.env.VUE_APP_ROOT_API + "registry/vehicles/" + item.id + "/approve", headers: this.getHeaders(), data: { lp: r.value ? parseInt(r.value) : null } });
        Swal.fire("Zatwierdzono", "Pojazd dodany do wykazu", "success");
        this.fetchActive(); this.fetchPending(); this.fetchPendingCount();
      } catch (e) { Swal.fire("Błąd", e.response?.data?.message || "Błąd", "error"); }
    },
    statusMiasta(m) {
      const czesci = [m.nazwa, m.status];
      if (m.lp) czesci.push("Lp. " + m.lp);
      if (m.nrBoczny) czesci.push("nr boczny " + m.nrBoczny);
      return czesci.join(" — ");
    },
    async openCityDialog(item) {
      this.loadDrivers();
      // Świeże dane wpisu — lista miast musi być aktualna, inaczej podpowiemy
      // oddział, w którym auto właśnie zostało zgłoszone z innego stanowiska.
      try {
        const res = await axios({ method: "get", url: process.env.VUE_APP_ROOT_API + "registry/vehicles/" + item.id, headers: this.getHeaders() });
        this.cityItem = res.data.data;
      } catch (e) { this.cityItem = item; }
      this.cityData = { subCompanyId: "", nrBoczny: "", kierowca: this.cityItem.kierowca || "", dataZgloszenia: "" };
      this.cityDialog = true;
    },
    async submitCity() {
      if (!this.cityData.subCompanyId) return;
      try {
        const payload = { subCompanyId: this.cityData.subCompanyId };
        if (this.cityData.nrBoczny) payload.nrBoczny = this.cityData.nrBoczny;
        if (this.cityData.kierowca) payload.kierowca = this.cityData.kierowca;
        if (this.cityData.dataZgloszenia) payload.dataZgloszenia = this.cityData.dataZgloszenia;
        await axios({ method: "post", url: process.env.VUE_APP_ROOT_API + "registry/vehicles/" + this.cityItem.id + "/cities", headers: this.getHeaders(), data: payload });
        Swal.fire("Zgłoszono", "Pojazd czeka na akceptację w wybranym mieście", "success");
        this.cityDialog = false;
        this.fetchActive(); this.fetchPending(); this.fetchPendingCount();
      } catch (e) { Swal.fire("Błąd", e.response?.data?.message || "Nie udało się zgłosić", "error"); }
    },
    openRejectDialog(item) { this.rejectItem = item; this.rejectReason = ""; this.rejectDialog = true; },
    async submitReject() {
      if (!this.rejectReason) return;
      try {
        await axios({ method: "post", url: process.env.VUE_APP_ROOT_API + "registry/vehicles/" + this.rejectItem.id + "/reject", headers: this.getHeaders(), data: { powodOdrzucenia: this.rejectReason } });
        Swal.fire("Odrzucono", "Wpis został odrzucony", "info");
        this.rejectDialog = false; this.fetchPending(); this.fetchPendingCount();
      } catch (e) { Swal.fire("Błąd", "Nie udało się odrzucić", "error"); }
    },
    async confirmDeactivate(item) {
      const wielemiast = (item.miasta || []).length > 1;
      const r = await Swal.fire({
        title: "Dezaktywuj pojazd?",
        html: wielemiast
          ? `Pojazd <b>${item.nrRejestracyjny}</b> zostanie wypisany z wykazu tego miasta.<br>W pozostałych miastach zostaje bez zmian.`
          : `Pojazd <b>${item.nrRejestracyjny}</b> zostanie przeniesiony do archiwum.`,
        icon: "warning", showCancelButton: true, confirmButtonColor: "#d33", confirmButtonText: "Dezaktywuj", cancelButtonText: "Anuluj",
      });
      if (!r.isConfirmed) return;
      try {
        const res = await axios({ method: "post", url: process.env.VUE_APP_ROOT_API + "registry/vehicles/" + item.id + "/deactivate", headers: this.getHeaders() });
        Swal.fire("Dezaktywowano", res.data?.data?.message || "Pojazd w archiwum", "success");
        this.fetchActive(); this.fetchArchive();
      } catch (e) { Swal.fire("Błąd", "Nie udało się dezaktywować", "error"); }
    },
    async confirmRestore(item) {
      const r = await Swal.fire({ title: "Przywróć pojazd?", html: `Przywrócić <b>${item.nrRejestracyjny}</b> do wykazu?`, icon: "question", showCancelButton: true, confirmButtonText: "Przywróć", cancelButtonText: "Anuluj" });
      if (!r.isConfirmed) return;
      try {
        await axios({ method: "post", url: process.env.VUE_APP_ROOT_API + "registry/vehicles/archive/" + item.id + "/restore", headers: this.getHeaders() });
        Swal.fire("Przywrócono", "Pojazd w wykazie", "success");
        this.fetchActive(); this.fetchArchive();
      } catch (e) { Swal.fire("Błąd", "Nie udało się przywrócić", "error"); }
    },
    async downloadExcel() {
      try {
        const params = {};
        if (this.selectedSubCompanyId) params.subCompanyId = this.selectedSubCompanyId;
        const res = await axios({ method: "get", url: process.env.VUE_APP_ROOT_API + "registry/vehicles/export/excel", headers: this.getHeaders(), params, responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a"); a.href = url; a.download = "wykaz_pojazdow.xlsx"; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      } catch (e) { Swal.fire("Błąd", "Nie udało się pobrać Excel", "error"); }
    },
    async downloadPdf() {
      try {
        const params = {};
        if (this.selectedSubCompanyId) params.subCompanyId = this.selectedSubCompanyId;
        const res = await axios({ method: "get", url: process.env.VUE_APP_ROOT_API + "registry/vehicles/export/pdf", headers: this.getHeaders(), params, responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a"); a.href = url; a.download = "wykaz_pojazdow.pdf"; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      } catch (e) { Swal.fire("Błąd", "Nie udało się pobrać PDF", "error"); }
    },
  },
};
</script>

<style scoped>
.wykazpojazdy-page { background: #181C28; min-height: calc(100vh - 56px); padding: 16px 16px 40px; margin: -24px; color: #fff; }
.page-header { padding: 4px 4px 18px; }
.page-eyebrow { font-size: 12px; color: #6E7691; font-weight: 500; }
.page-title { font-size: 22px; font-weight: 800; color: #fff; margin: 2px 0 0; letter-spacing: -0.02em; }
.wykazpojazdy-page >>> .v-card { background: #232838 !important; border: 1px solid rgba(255,255,255,0.06) !important; border-radius: 18px !important; box-shadow: none !important; color: #fff !important; }
.wykazpojazdy-page >>> .v-card .v-card__title { color: #fff !important; padding: 16px 20px 8px !important; font-weight: 700 !important; font-size: 15px !important; }
.wykazpojazdy-page >>> .v-text-field--outlined fieldset, .wykazpojazdy-page >>> .v-select--outlined fieldset { border-color: rgba(255,255,255,0.1) !important; }
.wykazpojazdy-page >>> .v-text-field--outlined .v-input__slot, .wykazpojazdy-page >>> .v-select--outlined .v-input__slot { background: #181C28 !important; border-radius: 12px !important; }
.wykazpojazdy-page >>> .v-label { color: #6E7691 !important; }
.wykazpojazdy-page >>> .v-text-field input, .wykazpojazdy-page >>> .v-select__selection { color: #fff !important; }
.wykazpojazdy-page >>> .v-data-table { background: #232838 !important; border: 1px solid rgba(255,255,255,0.06) !important; border-radius: 18px !important; color: #fff !important; box-shadow: none !important; }
.wykazpojazdy-page >>> .v-data-table thead tr th { background: #1F2434 !important; color: #6E7691 !important; font-size: 11px !important; font-weight: 700 !important; text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid rgba(255,255,255,0.06) !important; }
.wykazpojazdy-page >>> .v-data-table tbody tr { background: #232838 !important; }
.wykazpojazdy-page >>> .v-data-table tbody tr:hover { background: #2A3042 !important; }
.wykazpojazdy-page >>> .v-data-table tbody tr td { color: #fff !important; border-bottom: 1px solid rgba(255,255,255,0.04) !important; }
.wykazpojazdy-page >>> .v-data-footer { background: #1F2434 !important; color: #B0BEC5 !important; border-top: 1px solid rgba(255,255,255,0.06) !important; }
.wykazpojazdy-page >>> .v-toolbar { background: transparent !important; color: #fff !important; }
.wykazpojazdy-page >>> .v-tabs { background: transparent !important; }
.wykazpojazdy-page >>> .v-tab { color: #B0BEC5 !important; text-transform: none !important; font-weight: 600 !important; }
.wykazpojazdy-page >>> .v-tab--active { color: #5478FF !important; }
.wykazpojazdy-page >>> .v-tabs-slider { background: #5478FF !important; }
.wykazpojazdy-page >>> .v-tabs-bar { background: transparent !important; }
.wykazpojazdy-page >>> .v-btn:not(.v-btn--icon) { border-radius: 10px !important; text-transform: none !important; font-weight: 600 !important; }
.wykazpojazdy-page >>> .v-chip { border-radius: 999px !important; font-weight: 700 !important; }
.wykazpojazdy-page >>> .v-divider { border-color: rgba(255,255,255,0.06) !important; }
.wykazpojazdy-page >>> .v-progress-linear { background: rgba(255,255,255,0.06) !important; }
.wykazpojazdy-page >>> .v-dialog .v-card { background: #232838 !important; }
.wykazpojazdy-page >>> .v-tabs-items, .wykazpojazdy-page >>> .v-window { background: transparent !important; }
</style>
